/**
 * Files being uploaded (slice 5b spec §6.1, §6.2): each one's state, its
 * kept file once it's in, or why it isn't. One at a time, in the order
 * picked: a phone on a slow line uploads them as fast in turn as together.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { errorHint, upload, type Uploaded } from "../../app/api";
import { refusalFor } from "./kinds";

export type Upload = {
  key: string;
  name: string;
  size: number;
  mime: string;
  state: "uploading" | "done" | "failed";
  file?: Uploaded;
  hint?: string;
  /** A picked image's own bytes to show while it uploads (an object URL, let go with its row). */
  preview?: string;
};

/**
 * Uploads `files` in turn; `then` runs with each kept file (New doc's doc,
 * say) and may refuse it with a thrown error, which marks it failed.
 */
export function useUploads(then?: (file: Uploaded) => Promise<void>) {
  const [list, setList] = useState<Upload[]>([]);
  const queue = useRef(Promise.resolve());
  // Each row's object URL, let go when it's removed, cleared or unmounted.
  const previews = useRef(new Map<string, string>());
  const release = useCallback((key?: string) => {
    for (const [k, url] of previews.current) {
      if (key !== undefined && k !== key) continue;
      URL.revokeObjectURL(url);
      previews.current.delete(k);
    }
  }, []);
  useEffect(() => () => release(), [release]);
  // Each upload's abort: removing its chip stops it (Devin on #435).
  const aborts = useRef(new Map<string, AbortController>());
  const patch = useCallback(
    (key: string, change: Partial<Upload>) =>
      setList((l) => l.map((u) => (u.key === key ? { ...u, ...change } : u))),
    [],
  );
  const add = useCallback(
    (files: File[]) => {
      const rows = files.map((f) => ({
        key: crypto.randomUUID(),
        name: f.name,
        size: f.size,
        mime: f.type,
        refused: refusalFor(f),
        blob: f,
      }));
      const shown = rows.map((row) => {
        if (row.refused || !row.mime.startsWith("image/")) return row;
        const preview = URL.createObjectURL(row.blob);
        previews.current.set(row.key, preview);
        return { ...row, preview };
      });
      setList((l) => [
        ...l,
        ...shown.map(({ refused, blob: _b, ...u }) => ({
          ...u,
          state: refused ? ("failed" as const) : ("uploading" as const),
          ...(refused ? { hint: refused } : {}),
        })),
      ]);
      for (const row of rows) {
        if (row.refused) continue;
        const abort = new AbortController();
        aborts.current.set(row.key, abort);
        queue.current = queue.current.then(async () => {
          if (abort.signal.aborted) return;
          try {
            const file = await upload(row.blob, abort.signal);
            await then?.(file);
            patch(row.key, { state: "done", file });
          } catch (error) {
            if (!abort.signal.aborted) patch(row.key, { state: "failed", hint: errorHint(error) });
          } finally {
            aborts.current.delete(row.key);
          }
        });
      }
      return queue.current;
    },
    [patch, then],
  );
  const remove = useCallback(
    (key: string) => {
      aborts.current.get(key)?.abort();
      release(key);
      setList((l) => l.filter((u) => u.key !== key));
    },
    [release],
  );
  /** Files kept already, back as done (an Edited follow-up's); one here already isn't added twice. */
  const put = useCallback(
    (files: Uploaded[]) =>
      setList((l) => [
        ...l,
        // An effect run twice puts the same files twice (Codex on #435): once each.
        ...files
          .filter((file) => !l.some((u) => u.file?.id === file.id))
          .map((file) => ({
            key: crypto.randomUUID(),
            name: file.name,
            size: file.size,
            mime: file.mime,
            state: "done" as const,
            file,
          })),
      ]),
    [],
  );
  const clear = useCallback(() => {
    release();
    setList([]);
  }, [release]);
  return {
    list,
    add,
    remove,
    put,
    clear,
    /** Kept and ready to send. */
    ready: list.flatMap((u) => (u.state === "done" && u.file ? [u.file] : [])),
    busy: list.some((u) => u.state === "uploading"),
  };
}
